<template lang="html">
  <MainLayout>
    <div>
      <BackButton text="Back to Project" :route="$routes.project(projectId)" />
      <v-container max-width="1400">
        <div class="text-center mb-10">
          <h2 class="mb-3">
            {{ kpi.id }}. {{ StringManipulation.methods.clearAndCapitalize(kpi.name) }}
            <v-chip
                v-if="!kpi.state"
                variant="flat"
                color="red"
                prepend-icon="fas fa-exclamation-circle pl-4 pr-3"
            >
              Incomplete KPI
            </v-chip>
          </h2>
          <h3 class="text-grey-darken-2 font-weight-light">Functional Unit: <b class="font-weight-bold">{{ kpiType?.functional_unit ?? '-' }}</b> </h3>

        </div>

        <!-- AGGREGATION LEVEL VALUES -->
        <DividerWithText class="my-4" text="Value per aggregation level" color="#abc" text-color="#479" />
        <v-table hover v-if="valuePerLevel.length > 0">
          <thead>
            <tr>
              <th
                class="text-left"
                style="background-color: #4a5876"
                v-for="head in headers1"
                :key="head"
              >
                <h4 class="text-white">{{ head.title }}</h4>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in valuePerLevel" :key="item.name">
              <td v-for="head in headers1">{{ item[head.key] }}</td>
            </tr>
          </tbody>
        </v-table>
        <v-row v-else>
          <v-col cols="3">
            <v-alert variant="tonal" color="#555" icon="fas fa-info-circle">
              No values per aggregation level
            </v-alert>
          </v-col>
        </v-row>

        <!-- DIMENSION AND SECTOR SCORES -->
        <NestedHeaderTable
          class="mt-10"
          v-if="dimension.name || sectors.length > 0"
          :levels="aggregationLevels"
          :sectors="sectors"
          :dimension="dimension"
        ></NestedHeaderTable>
        <v-row v-else>
          <v-col cols="3">
            <v-alert variant="tonal" color="#555" icon="fas fa-info-circle">
              No dimensions nor sectors specified.
            </v-alert>
          </v-col>
        </v-row>

        <!-- MARGINS -->
        <DividerWithText class="mt-8" text="Margins" text-color="#479" color="#abc" />
        <v-row v-if="kpi.likert === true">
          <v-col class="mt-3" cols="4">
            <v-alert variant="tonal" color="#555" icon="fas fa-info-circle">
              Likert scale is used.
            </v-alert>
          </v-col>
        </v-row>

        <div v-else-if="margins.length > 0" class="">
          <v-row class="mt-2" v-if="!(margins[0].level === 'common')">
            <v-col cols="3">
              <v-select
                  label="Aggregation Level:"
                  v-model="selectedLevel"
                  :items="optionLevels"
                  density="compact"
                  variant="outlined" />
            </v-col>
          </v-row>
          <v-data-table
            hover
            :items="scale"
            :headers="headers2"
            class="elevation-1 mt-4"
            item-value="scale"
            hide-default-footer
          >
            <template v-slot:item="{ item }">
              <tr :style="{ backgroundColor: getColor(item.scale) }">
                <td>{{ item.scale }}</td>
                <td>{{ item.value }}</td>
              </tr>
            </template>
          </v-data-table>
        </div>

        <v-row class="mt-15 text-center">
          <v-col>
            <v-btn class="px-lg-16"
                   variant="flat"
                   text="Edit"
                   prepend-icon="mr-3 fas fa-pen"
                   color="blue-darken-4"
                   @click="router.get($routes.edit_project_kpi(projectId, props.kpi.id))"
            />
          </v-col>
        </v-row>

      </v-container>
    </div>
  </MainLayout>
</template>

<script setup>
// import { VDataTable } from 'vuetify/labs/VDataTable'
import MainLayout from "../../Layouts/Main.vue";
import NestedHeaderTable from "../../Shared/NestedHeaderTable.vue";
import MarginsFunctions from "../../Mixins/MarginsFunctions";
import {computed, onMounted, ref} from "vue";
import StringManipulation from "@/Mixins/StringManipulation";
import BackButton from "@/Shared/BackButton.vue";
import * as $routes from "@/packs/routes";
import DividerWithText from "@/Shared/DividerWithText.vue";
import {router} from "@inertiajs/vue3";

const props = defineProps({
  projectId: {
    type: Number,
    required: true,
  },
  kpi: {
    type: Object,
    required: true,
  },
  kpiType: {
    type: Object,
    required: true,
  },
  aggregationLevels: {
    type: Array,
    required: true,
  },
  sectors: {
    type: Array,
    required: true,
  },
  dimension: {
    type: Object,
    required: true,
  },
  margins: {
    type: Array,
    required: true,
  },
});
const selectedLevel = ref("");
const itemsPerPage = ref(10);
const headers1 = [
  { title: "Level", key: "level" },
  { title: "Value", key: "value" },
];
const headers2 = [
  { title: "Scale", key: "scale" },
  { title: "Value", key: "value" },
];
const scale = computed(() => {
  if (selectedLevel.value !== "") {
    const marginsIndex = props.margins.findIndex(
      (m) => m.level === selectedLevel.value
    );
    const margins = props.margins[marginsIndex];

    return MarginsFunctions.methods.showPageMargins([
      margins["m0"],
      margins["m1"],
      margins["m2"],
      margins["m3"],
    ]);
  }
});

const valuePerLevel = computed(() => {
  if (!props.kpi.state && props.dimension.agg.length === 0) return [];
  let arr = [];
  for (let i = 0; i < props.aggregationLevels.length; i++)
    arr.push({
      level: StringManipulation.methods.capitalize(props.aggregationLevels[i]),
      value: props.dimension.agg.find(
        (l) => l.name === props.aggregationLevels[i]
      ).value,
    });
  return arr;
});

const optionLevels = computed(() => {
  return props.aggregationLevels;
});

onMounted(() => {
  if (props.margins.length > 0) {
    selectedLevel.value = props.margins[0].level;
  }
});
function getColor(value) {
  if (value === "Failed") return "rgba(227,129,129,0.73)";
  else if (value === "Underachieved") return "rgba(255,186,54,0.67)";
  else if (value === "Achieved") return "#ece52eaa";
  else if (value === "Overachieved") return "rgba(213,255,152,0.67)";
  else return "rgba(147,209,147,0.73)";
}
</script>

<style lang="css" scoped>

tr {
  color: black !important;
}

tr th {
  font-size: 1rem;
}

a:hover {
  background-color: #4391b9;
  border-radius: 2px;
}

thead {
  background: #2b28d2;
  color: #111;
}
::v-deep thead tr th {
  background-color: #0d479c !important;
  font-weight: bold;
}
::v-deep thead tr {
  color: #fff !important;
  font-size: 1rem;
}
.v-table tr:nth-child(odd) {
  background-color: #f3f3f3;
}
</style>
